/**
 * Counts human-style input events since page load, for the "Map Unavailable" incident report: scripted browsers
 * (monitors, scrapers) usually fire none. Only counts are kept — never keys, coordinates, targets or content.
 * Listeners are passive and the counters are capped, so this costs nothing on normal pages.
 */

type InteractionKind = "pointer" | "key" | "scroll" | "touch"

const MAX_COUNT = 1000

const EVENT_KIND: Record<string, InteractionKind> = {
  pointermove: "pointer",
  pointerdown: "pointer",
  keydown: "key",
  wheel: "scroll",
  scroll: "scroll",
  touchstart: "touch",
}

const counts: Record<InteractionKind, number> = { pointer: 0, key: 0, scroll: 0, touch: 0 }
let started = false

const onEvent = (event: Event) => {
  const kind = EVENT_KIND[event.type]
  if (kind && counts[kind] < MAX_COUNT) counts[kind] += 1
}

/** Starts counting (idempotent). Call once at app boot. */
export function startInteractionCounter() {
  if (started || typeof window === "undefined") return
  started = true
  for (const type of Object.keys(EVENT_KIND)) {
    window.addEventListener(type, onEvent, { passive: true, capture: true })
  }
}

export type InteractionSnapshot = {
  pointerEvents: number
  keyEvents: number
  scrollEvents: number
  touchEvents: number
  interactionEvents: number
  msSinceLoad: number
}

/** Counts so far, and how long the page has been open. */
export function getInteractionSnapshot(): InteractionSnapshot {
  return {
    pointerEvents: counts.pointer,
    keyEvents: counts.key,
    scrollEvents: counts.scroll,
    touchEvents: counts.touch,
    interactionEvents: counts.pointer + counts.key + counts.scroll + counts.touch,
    msSinceLoad: Math.round(typeof performance !== "undefined" ? performance.now() : 0),
  }
}
